import { Check, Lock, Users, type LucideIcon } from 'lucide-react';
import type { DraftDocumentVisibility } from '@/hooks/useDraftDocuments';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/NewButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const OPEN_MATTER_VISIBILITY: DraftDocumentVisibility = 'matter_write';

export function isPrivateDraftVisibility(visibility: DraftDocumentVisibility) {
  return visibility === 'private';
}

export function draftVisibilityLabel(visibility: DraftDocumentVisibility) {
  return isPrivateDraftVisibility(visibility) ? 'Private' : 'Open to matter';
}

export function draftVisibilityIcon(visibility: DraftDocumentVisibility): LucideIcon {
  return isPrivateDraftVisibility(visibility) ? Lock : Users;
}

export function DraftVisibilityMenu({
  align = 'end',
  canUpdateVisibility,
  className,
  isUpdatingVisibility = false,
  onVisibilityChange,
  showLabel = false,
  visibility,
}: {
  align?: 'start' | 'center' | 'end';
  canUpdateVisibility: boolean;
  className?: string;
  isUpdatingVisibility?: boolean;
  onVisibilityChange: (visibility: DraftDocumentVisibility) => void;
  showLabel?: boolean;
  visibility: DraftDocumentVisibility;
}) {
  const isPrivate = isPrivateDraftVisibility(visibility);
  const visibilityLabel = draftVisibilityLabel(visibility);
  const VisibilityIcon = draftVisibilityIcon(visibility);
  const visibilityTooltip = canUpdateVisibility
    ? visibilityLabel
    : `${visibilityLabel}. Only the owner can change sharing.`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="bare"
          size="sm"
          icon={VisibilityIcon}
          text={showLabel ? visibilityLabel : undefined}
          loading={isUpdatingVisibility}
          className={cn('px-1 text-slate-600 hover:text-slate-950', className)}
          aria-label={visibilityTooltip}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-56">
        <DropdownMenuItem
          disabled={!canUpdateVisibility || isUpdatingVisibility || isPrivate}
          onSelect={() => onVisibilityChange('private')}
        >
          <Lock className="h-4 w-4" />
          <span className="min-w-0 flex-1">
            <span className="block">{draftVisibilityLabel('private')}</span>
            <span className="block text-xs text-slate-500">Only you</span>
          </span>
          {isPrivate ? <Check className="h-4 w-4 text-slate-500" /> : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canUpdateVisibility || isUpdatingVisibility || !isPrivate}
          onSelect={() => onVisibilityChange(OPEN_MATTER_VISIBILITY)}
        >
          <Users className="h-4 w-4" />
          <span className="min-w-0 flex-1">
            <span className="block">{draftVisibilityLabel(OPEN_MATTER_VISIBILITY)}</span>
            <span className="block text-xs text-slate-500">Members can view and edit</span>
          </span>
          {!isPrivate ? <Check className="h-4 w-4 text-slate-500" /> : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
